import type { ContactWithId, Interaction, Profile, SchemaField } from './types';

export function parseDateValue(value: string): Date {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
}

export function weekBounds(now = new Date()): { start: number; end: number } {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    start.setDate(start.getDate() - (start.getDay() + 6) % 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start: start.getTime(), end: end.getTime() };
}

export function openNextStep(interactions: Interaction[]): Interaction | undefined {
    const latest = [...interactions].reverse().find(i => i.nextStep);
    return latest?.nextStepCompleted ? undefined : latest;
}

/* CSV records may contain quoted newlines, commas and escaped quotes. */
export function parseCSV(text: string): { headers: string[]; rows: string[][] } {
    const result: string[][] = [];
    let row: string[] = [], cell = '', quoted = false;
    const finishRow = () => {
        row.push(cell.trim());
        if (row.some(value => value)) result.push(row);
        row = []; cell = '';
    };
    text = text.replace(/^\uFEFF/, '');
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '"') {
            if (quoted && text[i + 1] === '"') { cell += '"'; i++; }
            else quoted = !quoted;
        } else if (char === ',' && !quoted) { row.push(cell.trim()); cell = ''; }
        else if ((char === '\n' || char === '\r') && !quoted) {
            finishRow();
            if (char === '\r' && text[i + 1] === '\n') i++;
        } else cell += char;
    }
    if (quoted) throw new Error('invalid_csv');
    finishRow();
    return result.length < 2 ? { headers: [], rows: [] } : { headers: result[0], rows: result.slice(1) };
}

export function csvCell(value: string): string {
    // Quoting alone does not stop spreadsheet formula execution.
    const safe = /^[\s\uFEFF]*[=+@-]/.test(value) ? "'" + value : value;
    return '"' + safe.replace(/"/g, '""') + '"';
}

export type Backup = {
    app: 'contextos'; version?: 1; schema: SchemaField[]; contacts: ContactWithId[];
    interactions: Record<string, Interaction[]>; profile?: Profile | null;
};

const record = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const string = (value: unknown): value is string => typeof value === 'string';
const date = (value: unknown): value is string => string(value) && !Number.isNaN(new Date(value).getTime());
const dateOnly = (value: unknown): value is string => string(value) && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(parseDateValue(value).getTime()) &&
    `${parseDateValue(value).getFullYear()}-${String(parseDateValue(value).getMonth() + 1).padStart(2, '0')}-${String(parseDateValue(value).getDate()).padStart(2, '0')}` === value;

/* Validate the entire file before any browser records are written. */
export function validateBackup(value: unknown): Backup {
    const invalid = (): never => { throw new Error('invalid_backup'); };
    if (!record(value) || value.app !== 'contextos' || (value.version !== undefined && value.version !== 1) ||
        !Array.isArray(value.schema) || !Array.isArray(value.contacts) || !record(value.interactions)) return invalid();
    const fieldIds = new Set<string>(), contactIds = new Set<string>();
    for (const field of value.schema) {
        if (!record(field) || !string(field.id) || !field.id || !string(field.label) || fieldIds.has(field.id)) return invalid();
        fieldIds.add(field.id);
    }
    for (const contact of value.contacts) {
        if (!record(contact) || !string(contact.id) || !contact.id || contactIds.has(contact.id) ||
            !string(contact.name) || !contact.name.trim() || !Array.isArray(contact.fields) ||
            (contact.cadence !== undefined && (typeof contact.cadence !== 'number' || !Number.isFinite(contact.cadence) || contact.cadence <= 0))) return invalid();
        contactIds.add(contact.id);
        const seen = new Set<string>();
        for (const field of contact.fields) {
            if (!record(field) || !string(field.id) || !field.id || !string(field.value) || seen.has(field.id)) return invalid();
            seen.add(field.id);
        }
        if (!Object.hasOwn(value.interactions, contact.id) || !Array.isArray(value.interactions[contact.id])) return invalid();
        for (const item of value.interactions[contact.id] as unknown[]) {
            if (!record(item) || !date(item.date) || !string(item.notes) ||
                (item.outcome !== undefined && !['good', 'neutral', 'tense'].includes(item.outcome as string)) ||
                (item.nextStep !== undefined && !string(item.nextStep)) ||
                (item.nextStepDue !== undefined && (!dateOnly(item.nextStepDue) || !item.nextStep)) ||
                (item.nextStepCompleted !== undefined && typeof item.nextStepCompleted !== 'boolean')) return invalid();
        }
    }
    if (Object.keys(value.interactions).some(id => !contactIds.has(id))) return invalid();
    if (value.profile != null && (!record(value.profile) || Object.values(value.profile).some(v => !string(v)))) return invalid();
    return value as Backup;
}
